import React, { useState, useRef } from 'react';
import axios, { API } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileArrowUp, FilePdf, Image, CheckCircle, Spinner,
  X, Warning, CloudArrowUp
} from '@phosphor-icons/react';

export const OnboardingDocuments = ({ uploadedDocs, setUploadedDocs }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(f => {
      const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      return allowed.includes(f.type) && f.size <= 10 * 1024 * 1024;
    });

    if (validFiles.length === 0) return;

    setUploading(true);

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'report');

        const { data } = await axios.post(`${API}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setUploadedDocs(prev => [...prev, {
          id: data.id,
          filename: data.filename,
          file_type: data.file_type,
          extraction_status: data.extraction_status,
        }]);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    setUploading(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeDoc = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`);
      setUploadedDocs(prev => prev.filter(d => d.id !== docId));
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="onboarding-documents">
      <h2 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Got any recent documents?
      </h2>
      <p className="text-sm text-slate-500 mb-1">
        Kindra will read and explain your reports so you don't have to wait weeks to understand them.
      </p>
      <p className="text-xs text-slate-400 mb-6">
        Lab results, discharge summaries, specialist letters — completely optional. You can always add documents later.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-[#235347] bg-[#235347]/5'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
        }`}
        data-testid="onboarding-doc-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Spinner size={32} className="animate-spin text-[#235347]" />
            <p className="text-sm text-slate-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <CloudArrowUp size={40} className="text-slate-300" weight="duotone" />
            <div>
              <p className="text-sm font-medium text-slate-600">Tap to select files</p>
              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 10MB each</p>
            </div>
          </div>
        )}
      </div>

      {/* Uploaded files */}
      {uploadedDocs.length > 0 && (
        <div className="mt-5 space-y-2">
          <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Uploaded</p>
          <AnimatePresence>
            {uploadedDocs.map((doc) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
                data-testid="onboarding-uploaded-doc"
              >
                {doc.file_type?.includes('pdf')
                  ? <FilePdf size={20} className="text-red-500 flex-shrink-0" />
                  : <Image size={20} className="text-blue-500 flex-shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 truncate">{doc.filename}</p>
                  <p className="text-[10px] text-slate-400">
                    {doc.extraction_status === 'analyzing' && 'Analyzing...'}
                    {doc.extraction_status === 'completed' && 'Analysis complete'}
                    {doc.extraction_status === 'needs_review' && 'Ready for review'}
                    {doc.extraction_status === 'pending' && 'Queued'}
                    {doc.extraction_status === 'failed' && 'Analysis could not complete'}
                  </p>
                </div>
                {doc.extraction_status === 'analyzing' && (
                  <Spinner size={16} className="animate-spin text-[#235347] flex-shrink-0" />
                )}
                {(doc.extraction_status === 'completed' || doc.extraction_status === 'needs_review') && (
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                )}
                {doc.extraction_status === 'failed' && (
                  <Warning size={16} className="text-amber-500 flex-shrink-0" />
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); removeDoc(doc.id); }}
                  className="p-1 text-slate-300 hover:text-red-400 transition-colors flex-shrink-0"
                  data-testid="remove-onboarding-doc"
                >
                  <X size={14} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* What happens */}
      <div className="mt-6 bg-slate-50 rounded-xl p-4">
        <p className="text-xs font-medium text-slate-500 mb-2">What happens when you upload?</p>
        <ul className="space-y-1.5">
          <li className="text-xs text-slate-500 flex items-start gap-2">
            <span className="text-[#235347] mt-0.5">1.</span>
            Kindra reads the document and extracts key health information
          </li>
          <li className="text-xs text-slate-500 flex items-start gap-2">
            <span className="text-[#235347] mt-0.5">2.</span>
            You review what was found — nothing is saved without your approval
          </li>
          <li className="text-xs text-slate-500 flex items-start gap-2">
            <span className="text-[#235347] mt-0.5">3.</span>
            Approved information enriches your health record
          </li>
        </ul>
      </div>
    </div>
  );
};
